/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
} from "../../CommonComponents";
import ReportModernViewWrapper from "../ReportModernViewWrapper";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadFastTrackReport } from "../../../../utils/downloadUtils";
import {
  ZONE_OPTIONS,
  REPORT_PAGINATION_DEFAULTS,
  REPORT_FILTER_LABELS,
  REPORT_PLACEHOLDERS,
  REPORT_DOWNLOAD_TEXT,
  REPORT_ERROR_MESSAGES,
  SERVICE_TYPE_OPTIONS,
  BOOKING_TYPE_OPTIONS
} from "../../../../constants/reportConstants";

const FastTrackReport = () => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "days"),
    moment()
  ]);
  const [hubList, setHubList] = useState([]);
  const [cpList, setCpList] = useState([]);
  const [selectedParentHubs, setSelectedParentHubs] = useState([]);
  const [selectedDestinationHubs, setSelectedDestinationHubs] = useState([]);
  const [selectedBookingCPs, setSelectedBookingCPs] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [selectedBookingTypes, setSelectedBookingTypes] = useState([]);
  const [pagination, setPagination] = useState({
    ...REPORT_PAGINATION_DEFAULTS
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [headerHubList, setHeaderHubList] = useState([]);
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

  // Fetch hubs from header/localStorage
  useEffect(() => {
    try {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];
      if (userHubs?.length > 0) {
        const headerHubs = [
          { label: "All", value: "ALL" },
          ...userHubs.map(hub => ({
            label: hub.hubName,
            value: hub.hubId
          }))
        ];
        setHeaderHubList(headerHubs);
      } else {
        // If no hubs assigned, show all available hubs but without "All" option
        setHeaderHubList([]);
      }
    } catch (e) {
      console.error("Error parsing user hubs for Fast Track Report", e);
      setHeaderHubList([]);
    }
  }, []);

  const handleMultiSelectChange = (values, setter, options = []) => {
    if (values.includes("ALL")) {
      const isLatestAll = values[values.length - 1] === "ALL";
      if (isLatestAll) {
        // Select all actual values (excluding the "ALL" option itself)
        const allActualValues = options
          .filter(opt => opt.value !== "ALL")
          .map(opt => opt.value);
        setter(allActualValues);
      } else {
        // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
        setter(values.filter(v => v !== "ALL"));
      }
    } else {
      setter(values);
    }
  };

  // Ref to track whether we already set the hub for the current login session
  const hubInitializedRef = useRef(false);


  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch {
        message.error(REPORT_ERROR_MESSAGES.FETCH_HUB_LIST_FAILED);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handleHubChanged = () => {
      const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
      const newActiveHubId = activeHub?.hubId;
      setActiveHubId(newActiveHubId || "ALL_HUBS");
      if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
        setSelectedParentHubs([newActiveHubId]);
      } else {
        setSelectedParentHubs(["ALL"]);
      }
    };

    window.addEventListener("hubChanged", handleHubChanged);
    return () => window.removeEventListener("hubChanged", handleHubChanged);
  }, []);

  // Set default hub based on logged-in user — always enforced for hub-restricted roles
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
    const role = user?.roles?.[0];
    setHubUser(role);

    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
    const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

    if (!hubInitializedRef.current) {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];

      if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
        setSelectedParentHubs([currentActiveHubId]);
      } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
        setSelectedParentHubs([]);
      } else {
        setSelectedParentHubs(["ALL"]);
      }
      hubInitializedRef.current = true;
    }
  }, [headerHubList]); // Depend on headerHubList to ensure we have options available

  useEffect(() => {
    if (!selectedParentHubs || selectedParentHubs.length === 0) {
      setCpList([]); // clear CP list if no hub selected
      setSelectedBookingCPs([]); // Clear selected CPs when hub selection changes
      return;
    }

    (async () => {
      try {
        setCpLoading(true);
        const cpLists = await Promise.all(selectedParentHubs.map(hubId => getHubCPList(hubId)));
        const allCps = [...new Set(cpLists.flat())];
        setCpList(allCps);
      } catch (error) {
        console.error("Failed to fetch CP list", error);
        message.error(REPORT_ERROR_MESSAGES.FETCH_CP_LIST_FAILED);
        setCpList([]);
      } finally {
        setCpLoading(false);
      }
    })();
  }, [selectedParentHubs]);

  useEffect(() => {
    if (!hubUser) {
      return;
    }
    if (hubUser === "ROLE_HUBADMIN" && selectedParentHubs.length === 0) {
      return;
    }
    fetchReport();
    // eslint-disable-next-line
  }, [hubUser, selectedParentHubs, selectedDestinationHubs, selectedBookingCPs, selectedZones, selectedServiceTypes, selectedBookingTypes, dateRange, pagination.current, pagination.pageSize]);

  const fetchReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning(REPORT_ERROR_MESSAGES.SELECT_DATE_RANGE);
      return;
    }

    setLoading(true);
    try {
      // Resolve Parent Hubs (selectedParentHubs) for API logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedParentHubIds = selectedParentHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedParentHubs;

      const selectedParentHubData = activeHubOptions.filter(hub => resolvedParentHubIds?.includes(hub.value));
      const selectedDestHubData = hubList.filter(hub => selectedDestinationHubs?.includes(hub.value));

      const params = {
        size: pagination.pageSize,
        page: pagination.current - 1,
        fromDate: dateRange[0].format("YYYY-MM-DD"),
        toDate: dateRange[1].format("YYYY-MM-DD"),
        ...(resolvedParentHubIds?.length > 0 && { parentHubIds: resolvedParentHubIds }),
        ...(selectedDestinationHubs?.length > 0 && { destinationHubIds: selectedDestinationHubs }),
        ...(selectedBookingCPs?.length > 0 && { bookingCpIds: selectedBookingCPs }),
        ...(selectedZones?.length > 0 && { zones: selectedZones }),
        ...(selectedServiceTypes?.length > 0 && { serviceTypes: selectedServiceTypes }),
        ...(selectedBookingTypes?.length > 0 && { bookingTypes: selectedBookingTypes })
      };

      console.log("Fetching fast track report with params:", params);
      const response = await ReportsService.getFastTrackReport(params);
      console.log("Fast track report response:", response);

      if (response?.lastRefreshedAt) {
        setLastsynced(response.lastRefreshedAt);
      }
      if (response?.nextScheduledRefresh) {
        setNextScheduledRefresh(response.nextScheduledRefresh);
      }
      if (response?.data) {
        setReportData(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
          current: (response.page || 0) + 1,
          pageSize: response.size || 10
        }));
      } else {
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
        message.error("No data available");
      }
    } catch (error) {
      console.error("Error fetching fast track report:", error);
      message.error(error?.message || REPORT_ERROR_MESSAGES.FETCH_REPORT_FAILED);
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning(REPORT_ERROR_MESSAGES.SELECT_DATE_RANGE);
      return;
    }
    setDownloadLoading(true);
    try {
      // Resolve Parent Hubs (selectedParentHubs) for Download logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedParentHubIds = selectedParentHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedParentHubs;

      const selectedParentHubData = activeHubOptions.filter(hub => resolvedParentHubIds?.includes(hub.value));
      const selectedDestHubData = hubList.filter(hub => selectedDestinationHubs?.includes(hub.value));

      const downloadParams = {
        fromDate: dateRange[0].format("YYYY-MM-DD"),
        toDate: dateRange[1].format("YYYY-MM-DD"),
        ...(resolvedParentHubIds?.length > 0 && { parentHubIds: resolvedParentHubIds }),
        ...(selectedDestinationHubs?.length > 0 && { destinationHubIds: selectedDestinationHubs }),
        ...(selectedBookingCPs?.length > 0 && { bookingCpIds: selectedBookingCPs }),
        ...(selectedZones?.length > 0 && { zones: selectedZones }),
        ...(selectedServiceTypes?.length > 0 && { serviceTypes: selectedServiceTypes }),
        ...(selectedBookingTypes?.length > 0 && { bookingTypes: selectedBookingTypes })
      };

      await downloadFastTrackReport(downloadParams);
      message.success(REPORT_DOWNLOAD_TEXT.SUCCESS);
    } catch (error) {
      console.error("Error downloading fast track report:", error);
      message.error(REPORT_DOWNLOAD_TEXT.ERROR);
    } finally {
      setDownloadLoading(false);
    }
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      key: "index",
      width: 60,
      fixed: "left",
      align: "center",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Document No",
      dataIndex: "documentNo",
      key: "documentNo",
      sorter: true,
      width: 140,
      fixed: "left",
      align: "center",
      render: (text, record) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            const url = `/shipment-tracker?awb=${text}`;
            window.open(url, "_blank");
          }}
        >
          {text}
        </span>
      )
    },
    { title: "Booking Date", dataIndex: "bookingDate", key: "bookingDate", sorter: true, width: 120, align: "center" },
    { title: "Booking CP", dataIndex: "bookingCp", key: "bookingCp", sorter: true, width: 180, align: "center" },
    { title: "Source Hub", dataIndex: "parentHubName", key: "parentHubName", sorter: true, width: 180, align: "center" },
    { title: "From State", dataIndex: "fromState", key: "fromState", sorter: true, width: 130, align: "center" },
    { title: "To Pincode", dataIndex: "toPincode", key: "toPincode", sorter: true, width: 110, align: "center" },
    { title: "To City", dataIndex: "toCity", key: "toCity", sorter: true, width: 130, align: "center" },
    { title: "To State", dataIndex: "toState", key: "toState", sorter: true, width: 130, align: "center" },
    { title: "Destination Hub", dataIndex: "destinationHubName", key: "destinationHubName", sorter: true, width: 200, align: "center" },
    { title: "To Center", dataIndex: "toCenter", key: "toCenter", sorter: true, width: 150, align: "center" },
    { title: "Receiver Name", dataIndex: "receiverName", key: "receiverName", sorter: true, width: 150, align: "center" },
    { title: "Area Name", dataIndex: "areaName", key: "areaName", sorter: true, width: 130, align: "center" },
    { title: "Booking Type", dataIndex: "bookingType", key: "bookingType", sorter: true, width: 120, align: "center" },
    { title: "Service Type", dataIndex: "serviceType", key: "serviceType", sorter: true, width: 120, align: "center" },
    { title: "Document Type", dataIndex: "documentType", key: "documentType", sorter: true, width: 130, align: "center" },
    { title: "Travel By", dataIndex: "travelBy", key: "travelBy", sorter: true, width: 100, align: "center" },
    { title: "Weight (kg)", dataIndex: "bookingWeight", key: "bookingWeight", sorter: true, width: 110, align: "center" },
    { title: "Charges", dataIndex: "charges", key: "charges", sorter: true, width: 100, align: "center" },
    { title: "Zone", dataIndex: "zone", key: "zone", sorter: true, width: 100, align: "center" },
    { title: "Current Status", dataIndex: "status", key: "status", sorter: true, width: 120, align: "center" },
    { title: "Current Status Time", dataIndex: "deliveryDate", key: "deliveryDate", sorter: true, width: 135, align: "center" },
    { title: "Current Premise Name", dataIndex: "currentPremiseName", key: "currentPremiseName", sorter: true, width: 150, align: "center" },
    { title: "POD", dataIndex: "pod", key: "pod", sorter: true, width: 120, align: "center" },


  ];

  return (
    <ReportModernViewWrapper
      title="Fast Track / Value Plus Report"
      newReportUrl="/reports/fast-track-value-plus-report/"
      lastsynced={lastsynced}
      nextScheduledRefresh={nextScheduledRefresh}
    >
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Date Range</div>
            <CalendarComponent
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={(value) => {
                setDateRange(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Parent Hub</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Select Parent Hubs"
              options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
              onChange={(val) => {
                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                handleMultiSelectChange(val, setSelectedParentHubs, baseOptions);
                setSelectedBookingCPs([]); // Clear CP selection when hubs change
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedParentHubs}
              allowClear
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Destination Hub</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Select Destination Hubs"
              options={hubList}
              onChange={(val) => {
                setSelectedDestinationHubs(val);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedDestinationHubs}
              mode="multiple"
              allowClear
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Booking CP</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder={cpLoading ? REPORT_PLACEHOLDERS.LOADING_CPS : cpList.length === 0 ? REPORT_PLACEHOLDERS.SELECT_HUB_FIRST : "Select Booking CP"}
              options={cpList}
              onChange={(value) => {
                setSelectedBookingCPs(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedBookingCPs}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              disabled={cpLoading || cpList.length === 0}
              loading={cpLoading}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">{REPORT_FILTER_LABELS.ZONES}</div>
            <DropDownComponent
              placeholder={REPORT_PLACEHOLDERS.SELECT_ZONES}
              options={ZONE_OPTIONS}
              onChange={(value) => {
                setSelectedZones(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedZones}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Service Types</div>
            <DropDownComponent
              placeholder="Service Types"
              options={SERVICE_TYPE_OPTIONS.filter(opt => opt.value !== "ALL")}
              onChange={(value) => {
                setSelectedServiceTypes(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedServiceTypes}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Booking Types</div>
            <DropDownComponent
              placeholder="Select Booking Types"
              options={BOOKING_TYPE_OPTIONS.filter(opt => opt.value !== "ALL")}
              onChange={(value) => {
                setSelectedBookingTypes(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedBookingTypes}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              onClick={handleDownload}
              loading={downloadLoading}
              icon={<DownloadOutlined />}
              disabled={!dateRange || !dateRange[0] || !dateRange[1]}
            >
              {REPORT_DOWNLOAD_TEXT.BUTTON}
            </Button>
          </Col>
        </Row>

        <div className="table-section">
          <TableComponent
            columns={columns}
            dataSource={reportData}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="documentNo"
            scroll={{ x: 2000 }}
          />
        </div>
      </Card>
    </ReportModernViewWrapper>
  );
};

export default FastTrackReport;
